// @mui
import {
  Button,
  Card,
  Container,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  Tooltip,
} from '@mui/material';
import { paramCase } from 'change-case';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { deleteCompany, getCompaniesSlice } from 'src/redux/slices/company';
import GenericTableToolbar from 'src/sections/@dashboard/GenericTableToolbar';
import CompanyTableRow from 'src/sections/@dashboard/companies/CompanyTableRow';
import { IUser } from '../../../@types/user';
import { useAuthContext } from '../../../auth/useAuthContext';
import ConfirmDialog from '../../../components/confirm-dialog';
import CustomBreadcrumbs from '../../../components/custom-breadcrumbs';
// _mock_
// components
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
import { useSettingsContext } from '../../../components/settings';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSelectedAction,
  useTable,
} from '../../../components/table';
// sections
import { useLocales } from '../../../locales';
import { useDispatch, useSelector } from '../../../redux/store';
// routes
import { PATH_DASHBOARD, PATH_PAGE } from '../../../routes/paths';
import { PERMISSIONS, ROLES, hasPermission } from '../../../utils/permissions';
import { getApeLink } from 'src/redux/slices/ape';

export default function CompaniesListPage() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    //
    selected,
    setSelected,
    onSelectRow,
    onSelectAllRows,
    onChangeDense,
  } = useTable();

  const { translate } = useLocales();

  const { themeStretch } = useSettingsContext();

  const { user } = useAuthContext();
  const navigate = useNavigate();

  if (!hasPermission(user as IUser, ROLES.ADMIN)) {
    navigate(PATH_PAGE.page403);
  }

  const TABLE_HEAD = [
    {
      id: 'name',
      label: `${translate('companyCreatePage.fields.name')}`,
      align: 'left',
    },
    {
      id: 'email',
      label: `${translate('companyCreatePage.fields.email')}`,
      align: 'left',
    },
    {
      id: 'siret',
      label: `${translate('companyCreatePage.fields.siret')}`,
      align: 'left',
    },
    {
      id: 'tva',
      label: `${translate('companyCreatePage.fields.tva')}`,
      align: 'left',
    },
    {
      id: 'rcs',
      label: `${translate('companyCreatePage.fields.rcs')}`,
      align: 'left',
    },
    { id: '' },
  ];

  if (
    !hasPermission(user as IUser, PERMISSIONS.companyEdit) &&
    !hasPermission(user as IUser, PERMISSIONS.companyDelete)
  ) {
    TABLE_HEAD.pop();
  }

  const dispatch = useDispatch();

  const { companies, isLoading, pagination } = useSelector(
    (state) => state.company
  );

  const [filterName, setFilterName] = useState('');

  const [openConfirm, setOpenConfirm] = useState(false);

  const [currentQuery, setCurrentQuery] = useState<any>({});

  const onChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      getCompaniesSlice(currentQuery, Number(event.target.value), page + 1 || 1)
    );
    setRowsPerPage(Number(event.target.value));
  };

  const onChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    dispatch(getCompaniesSlice(currentQuery, rowsPerPage, newPage + 1 || 1));
    setPage(newPage);
  };

  useEffect(() => {
    dispatch(getCompaniesSlice(currentQuery, rowsPerPage, page + 1 || 1));
  }, [dispatch]);

  const dataInPage = companies?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const isFiltered = filterName !== '';

  const isNotFound = !companies?.length && !!filterName;
  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleFilterName = () => {
    setPage(0);

    const query = {
      _or: [
        {
          name: {
            _ilike: `%${filterName}%`,
          },
        },
        {
          email: {
            _ilike: `%${filterName}%`,
          },
        },
      ],
    };

    console.log('qq', query);

    setCurrentQuery(query);

    dispatch(getCompaniesSlice(query, rowsPerPage, page + 1 || 1));
  };

  const handleDeleteRow = async (id: string) => {
    const deletedCompany = companies.find((row) => row.id === id);
    setSelected([]);

    if (deletedCompany) {
      await deleteCompany(deletedCompany.id);
    }

    setOpenConfirm(false);

    if (page > 0) {
      if (dataInPage.length < 2) {
        setPage(page - 1);
      }
    }

    const query = currentQuery || {};
    query._or = undefined;

    dispatch(getCompaniesSlice(query, rowsPerPage, page + 1 || 1));
  };

  const handleDeleteRows = async (selectedRows: string[]) => {
    const deleteRows = companies.filter((row) => selectedRows.includes(row.id));
    setSelected([]);
    await Promise.all(deleteRows.map((company) => deleteCompany(company.id)));

    console.log('to delete', deleteRows);

    if (page > 0) {
      if (selectedRows.length === dataInPage.length) {
        setPage(page - 1);
      } else if (selectedRows.length === companies.length) {
        setPage(0);
      } else if (selectedRows.length > dataInPage.length) {
        const newPage =
          Math.ceil((companies.length - selectedRows.length) / rowsPerPage) - 1;
        setPage(newPage);
      }
    }

    const query = currentQuery || {};
    query._or = undefined;

    dispatch(getCompaniesSlice(query, rowsPerPage, page + 1 || 1));
  };

  const handleEditRow = (id: string) => {
    navigate(PATH_DASHBOARD.companies.edit(paramCase(id)));
  };

  const handleResetFilter = () => {
    setPage(0);
    setFilterName('');

    const query = currentQuery || {};
    query._or = undefined;

    dispatch(getCompaniesSlice(query, rowsPerPage, page + 1 || 1));
  };

  const [apeLink, setApeLink] = useState<
    Array<{ id: string; apeCode: string }>
  >([]);

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await getApeLink();
        const formattedData = data.map((item: any) => ({
          id: item.id,
          apeCode: item.apeCode,
        }));
        setApeLink(formattedData);
      } catch (error) {
        console.error('Error fetching Ape Codes:', error);
      }
    }

    fetchData();
  }, []);

  return (
    <>
      <Helmet>
        <title> {`${translate('companyListPage.title')}`} </title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : 'lg'}>
        <CustomBreadcrumbs
          heading={`${translate('companyListPage.heading')}`}
          links={[
            {
              name: `${translate('dashboardName')}`,
              href: PATH_DASHBOARD.root,
            },
            { name: `${translate('company')}s` },
            { name: `${translate('list')}` },
          ]}
          action={
            <Button
              component={RouterLink}
              to={PATH_DASHBOARD.companies.new}
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              disabled={!hasPermission(user, PERMISSIONS.companyCreate)}
            >
              {`${translate('companyListPage.newCompany')}`}
            </Button>
          }
        />

        <Card>
          <Divider />

          <GenericTableToolbar
            isFiltered={isFiltered}
            filterName={filterName}
            onSearch={() => handleFilterName()}
            onFilterName={(event) => setFilterName(event.target.value)}
            onResetFilter={handleResetFilter}
          />

          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <TableSelectedAction
              dense={dense}
              numSelected={selected.length}
              rowCount={companies?.length}
              onSelectAllRows={(checked) =>
                onSelectAllRows(
                  checked,
                  companies.map((row) => row.id)
                )
              }
              action={
                <Tooltip title={`${translate('delete')}`}>
                  <IconButton color="primary" onClick={handleOpenConfirm}>
                    <Iconify icon="eva:trash-2-outline" />
                  </IconButton>
                </Tooltip>
              }
            />

            <Scrollbar>
              <Table size={dense ? 'small' : 'medium'} sx={{ minWidth: 800 }}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={companies?.length}
                  numSelected={selected.length}
                  onSelectAllRows={(checked) =>
                    onSelectAllRows(
                      checked,
                      companies.map((row) => row.id)
                    )
                  }
                />

                <TableBody>
                  {(isLoading ? [] : companies || []).map((row) => (
                    <CompanyTableRow
                      key={row.id}
                      row={row}
                      selected={selected.includes(row.id)}
                      onSelectRow={() => onSelectRow(row.id)}
                      onDeleteRow={() => handleDeleteRow(row.id)}
                      onEditRow={() => handleEditRow(row.id)}
                      apeLink={apeLink}
                    />
                  ))}

                  <TableNoData isNotFound={isNotFound} />
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={pagination?.total || companies?.length || 0}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            //
            dense={dense}
            onChangeDense={onChangeDense}
          />
        </Card>
      </Container>

      <ConfirmDialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        title={`${translate('delete')}`}
        content={
          <>
            {`${translate('sureDelete')}`} <strong> {selected.length} </strong>{' '}
            {`${translate('sureDelete2')}`}
          </>
        }
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              handleDeleteRows(selected);
              handleCloseConfirm();
            }}
          >
            {`${translate('delete')}`}
          </Button>
        }
      />
    </>
  );
}
